import { Button, Modal, OTPStep } from '@unified-checkout/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidth } from '../../hooks/useWidth';
import { MobileModal } from './MobileModal';

const TIMER = 60;
export type OnResendProps = {
  sendTo?: 'email' | 'phone';
  phone?: string;
};
type ClickOTPModalProps = {
  isOpen: boolean;
  onSubmit: (isValid: boolean, otp: string) => Promise<any>;
  onCancel: () => void;
  onResend: (props: OnResendProps) => Promise<void>;
  onSendToEmail: () => Promise<void>;
  type?: 'phone' | 'email';
  error: string;
  phone?: string;
  email?: string;
  isTopLayer?: boolean;
  lang?: string;
  isPhoneVerified: boolean;
  currentPhone?: string;
};

export const ClickOTPModal = ({
  isOpen,
  onSubmit,
  onCancel,
  onResend,
  onSendToEmail,
  type = 'phone',
  error,
  phone,
  email,
  isTopLayer,
  lang,
  isPhoneVerified,
  currentPhone
}: ClickOTPModalProps) => {
  const { t } = useTranslation('kitComponents');
  const isMobile = useWidth();

  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [showImediatelyResend, setShowImediatelyResend] = useState(false);

  const handleVerify = async () => {
    if (!otp || otp.length !== 6) return;
    setIsLoading(true);
    const res = await onSubmit(isValid, otp);
    if ((res as any)?.status === 423) setShowImediatelyResend(true);
    setIsLoading(false);
  };

  const autoVerifyHandler = () => {
    if(showImediatelyResend) return;
    setIsValid(true);
    handleVerify();
  };

  const handleResend = async () => {
    setIsLoading(true);
    await onResend({ sendTo: type === 'phone' ? 'phone' : 'email', phone: currentPhone});
    setTimerKey((prev) => prev + 1);
    setShowImediatelyResend(false);
    setIsLoading(false);
    setOtp('');
  };

  const handleSendToEmail = async () => {
    try {
      setIsLoading(true);
      await onSendToEmail();
      setShowImediatelyResend(false);
    } catch{
    } finally {
      setIsLoading(false);
      setOtp('');
    }
  };
  const method = type === 'phone' ? phone : email
  const title =
    type === 'phone'
      ? isPhoneVerified
        ? t('WELCOME_BACK')
        : t('VERIFY_YOUR_ACCOUNT')
      : t('CONFIRM_ITS_YOU');
  const text =
    (type === 'phone' ? `${t('LOGGED_IN_AS')} ${email} \n` : '') +
    (type === 'email' ? `${t('PLEASE_ENTER_CODE_TO_VERIFY', { method })}` : '') +
    (type === 'phone' ? (phone ?`${t('PLEASE_ENTER_CODE_TO_VERIFY', { method })}` : `${t('PLEASE_ENTER_CODE_TO_VERIFY_GENERIC')}`) :'');
  const showSendToEmail = type === 'phone';

  if (isMobile && isOpen) {
    return (
      <MobileModal title={title} text={text} onCancel={onCancel} hasBackButton>
        <section className="space-y-3 w-full [&>div>div>div]:w-full [&>div>div>div]:justify-between [&>div>div]:w-full">
          <OTPStep
            resendTimer={TIMER}
            onResendOTP={!showImediatelyResend ? handleResend : undefined}
            onValid={autoVerifyHandler}
            otp={otp}
            setOtp={setOtp}
            length={6}
            loading={isLoading}
            error={error}
            otpResetTimerKey={timerKey}
          />
          {showImediatelyResend && (
            <Button
              text={t('OTP_RESEND_BUTTON')}
              variant="link"
              color="primary"
              onClickHandler={handleResend}
              disabled={isLoading}
            />
          )}
          {showSendToEmail && (
            <Button
              color="primary"
              variant="link"
              onClickHandler={handleSendToEmail}
              text={t('EMAIL_INSTEAD')}
              disabled={isLoading}
            />
          )}
        </section>
      </MobileModal>
    );
  }

  return (
    <div className="[&>dialog]:whitespace-pre-line">
      <Modal
        open={isOpen}
        width="25rem"
        title={title}
        supportingText={text}
        btnsProps={[
          {
            type: 'submit',
            color: 'primary',
            text: t('SUBMIT_BTN'),
            onClickHandler: handleVerify,
            disabled: !isValid || isLoading || showImediatelyResend,
            loading: isLoading,
          },
          {
            type: 'button',
            color: 'secondary',
            text: t('CANCEL_BTN'),
            onClickHandler: onCancel,
            disabled: isLoading,
          },
        ]}
        position='center'
        isTopLayer={isTopLayer}
      >
        <div className="mx-[30px] my-6 [&>div]:w-fit w-fit">
          <div className="space-y-3">
            <OTPStep
              resendTimer={TIMER}
              onResendOTP={!showImediatelyResend ? handleResend : undefined}
              onValid={setIsValid}
              otp={otp}
              setOtp={setOtp}
              length={6}
              loading={isLoading}
              error={error}
              otpResetTimerKey={timerKey}
              lang={lang as 'ar' | 'en'}
            />
            {showImediatelyResend && (
              <Button
                text={t('OTP_RESEND_BUTTON')}
                variant="link"
                color="primary"
                onClickHandler={handleResend}
                disabled={isLoading}
              />
            )}
            {showSendToEmail && (
              <Button
                color="primary"
                variant="link"
                onClickHandler={handleSendToEmail}
                text={t('EMAIL_INSTEAD')}
                disabled={isLoading}
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
